import React from 'react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

/**
 * Footer Block Renderer
 * Variants: simple, multi_column, minimal
 */
export default function FooterRenderer({ props, variant }) {
  const {
    company_name = '',
    links = [],
    copyright_text = '\u00a9 2026 All rights reserved.',
    show_branding = true,
  } = props;

  const brandingEl = show_branding ? (
    <span className="text-xs text-stone-400">
      Built with{' '}
      <a
        href="https://nurturecrm.ai"
        target="_blank"
        rel="noopener noreferrer"
        className="text-stone-500 hover:text-stone-700 underline"
      >
        NurtureCRM
      </a>
    </span>
  ) : null;

  // Simple variant: single row with company name + copyright
  if (variant === 'simple') {
    return (
      <footer className="py-8 px-6 bg-stone-50 border-t border-stone-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {company_name && (
                <span
                  className="text-sm font-semibold text-stone-800"
                  style={{ fontFamily: HEADING_FONT }}
                >
                  {company_name}
                </span>
              )}
              {links.length > 0 && (
                <nav className="flex items-center gap-4">
                  {links.map((link, i) => (
                    <a
                      key={i}
                      href={link.url || '#'}
                      className="text-sm text-stone-500 hover:text-stone-700 transition-colors"
                      style={{ fontFamily: BODY_FONT }}
                    >
                      {link.label || link.text || 'Link'}
                    </a>
                  ))}
                </nav>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span
                className="text-sm text-stone-400"
                style={{ fontFamily: BODY_FONT }}
              >
                {copyright_text}
              </span>
              {brandingEl}
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Multi-column variant
  if (variant === 'multi_column') {
    // Group links by their group/column property if available
    const groupedLinks = {};
    links.forEach((link) => {
      const group = link.group || 'Links';
      if (!groupedLinks[group]) groupedLinks[group] = [];
      groupedLinks[group].push(link);
    });
    const groups = Object.entries(groupedLinks);

    return (
      <footer className="py-12 px-6 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company info column */}
            <div>
              {company_name && (
                <h3
                  className="text-lg font-bold mb-3"
                  style={{ fontFamily: HEADING_FONT }}
                >
                  {company_name}
                </h3>
              )}
              {brandingEl && <div className="mt-4">{brandingEl}</div>}
            </div>

            {/* Link columns */}
            {groups.length > 0 ? (
              groups.map(([groupName, groupLinks]) => (
                <div key={groupName}>
                  <h4
                    className="text-sm font-semibold text-stone-300 uppercase tracking-wider mb-3"
                    style={{ fontFamily: HEADING_FONT }}
                  >
                    {groupName}
                  </h4>
                  <ul className="space-y-2">
                    {groupLinks.map((link, i) => (
                      <li key={i}>
                        <a
                          href={link.url || '#'}
                          className="text-sm text-stone-400 hover:text-white transition-colors"
                          style={{ fontFamily: BODY_FONT }}
                        >
                          {link.label || link.text || 'Link'}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <div className="col-span-3 flex items-center justify-center">
                <span className="text-sm text-stone-500" style={{ fontFamily: BODY_FONT }}>
                  Add links to populate footer columns
                </span>
              </div>
            )}
          </div>

          {/* Bottom bar */}
          <div className="pt-6 border-t border-stone-700">
            <p
              className="text-sm text-stone-400 text-center"
              style={{ fontFamily: BODY_FONT }}
            >
              {copyright_text}
            </p>
          </div>
        </div>
      </footer>
    );
  }

  // Minimal variant: just copyright centered
  if (variant === 'minimal') {
    return (
      <footer className="py-6 px-6">
        <div className="max-w-6xl mx-auto text-center space-y-2">
          <p
            className="text-sm text-stone-400"
            style={{ fontFamily: BODY_FONT }}
          >
            {copyright_text}
          </p>
          {brandingEl}
        </div>
      </footer>
    );
  }

  // Fallback to simple
  return (
    <footer className="py-8 px-6 bg-stone-50 border-t border-stone-200">
      <div className="max-w-6xl mx-auto text-center space-y-2">
        {company_name && (
          <p className="text-sm font-semibold text-stone-800" style={{ fontFamily: HEADING_FONT }}>
            {company_name}
          </p>
        )}
        <p className="text-sm text-stone-400" style={{ fontFamily: BODY_FONT }}>
          {copyright_text}
        </p>
        {brandingEl}
      </div>
    </footer>
  );
}
